import { Box } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../../contexts/AuthContext';
import { Role } from '@lov/shared';
import {
  HeroSection,
  VenueTypeShowcase,
  HowItWorksSection,
  FeaturedVenuesCarousel,
  TestimonialsSection,
  LocationPickerSection,
  FAQSection,
  CTASection,
  MobileStickyBar,
} from '../components';
import type { FaqItem } from '../components/FAQSection';
import { ScrollTransition } from '../../../components/ui/illustrations/venue-themed';

const faqs: FaqItem[] = [
  {
    question: 'What types of venues can I book?',
    answer:
      'Lord of Venues lets you book Gaming Arenas, Snooker & Billiards halls, and Kids Play Areas. We are constantly adding new venue categories, so check back often for the latest options.',
    answerText:
      'Lord of Venues lets you book Gaming Arenas, Snooker & Billiards halls, and Kids Play Areas. We are constantly adding new venue categories, so check back often for the latest options.',
  },
  {
    question: 'How do I cancel a booking?',
    answer:
      'You can cancel a booking from the My Bookings section in your account. Cancellations made within the eligible window receive a full refund to your Lord of Venues wallet credits, which can be used on future bookings.',
    answerText:
      'You can cancel a booking from the My Bookings section in your account. Cancellations made within the eligible window receive a full refund to your Lord of Venues wallet credits, which can be used on future bookings.',
  },
  {
    question: 'Is my payment secure?',
    answer:
      'Yes, all payments on Lord of Venues are encrypted and processed through our PCI-compliant payment gateway. In the unlikely event of an issue, refunds are issued promptly to your wallet credits.',
    answerText:
      'Yes, all payments on Lord of Venues are encrypted and processed through our PCI-compliant payment gateway. In the unlikely event of an issue, refunds are issued promptly to your wallet credits.',
  },
  {
    question: 'Can I book for a group?',
    answer:
      'Absolutely! When a venue supports group or multi-player sessions, you will see the option to select player count during the booking flow. Group pricing may apply depending on the venue.',
    answerText:
      'Absolutely! When a venue supports group or multi-player sessions, you will see the option to select player count during the booking flow. Group pricing may apply depending on the venue.',
  },
  {
    question: 'What is the Loyalty Program?',
    answer:
      'Every booking earns you XP and wallet credits through our Loyalty Program. As you accumulate XP you unlock higher tiers — Silver, Gold, and Platinum — each offering greater rewards and exclusive perks.',
    answerText:
      'Every booking earns you XP and wallet credits through our Loyalty Program. As you accumulate XP you unlock higher tiers — Silver, Gold, and Platinum — each offering greater rewards and exclusive perks.',
  },
  {
    question: 'How do I list my venue?',
    answer:
      'Listing your venue is easy! Head to the List Your Venue page (/list-your-venue) or reach out to us directly at venues@lordofvenues.com and our team will help you get set up.',
    answerText:
      'Listing your venue is easy! Head to the List Your Venue page or reach out to us directly at venues@lordofvenues.com and our team will help you get set up.',
  },
];

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Lord of Venues',
  url: 'https://lordofvenues.com',
  logo: 'https://lordofvenues.com/logo.svg',
  description: 'Book gaming venues, snooker halls, and kids play areas across India',
  sameAs: [],
};

const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Lord of Venues',
  url: 'https://lordofvenues.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://lordofvenues.com/venues?search={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

function buildFaqPageJsonLd(faqItems: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answerText ?? (typeof faq.answer === 'string' ? faq.answer : faq.question),
      },
    })),
  };
}

export function LandingPage() {
  const { user, isAuthenticated } = useAuth();
  const faqPageJsonLd = buildFaqPageJsonLd(faqs);

  // Redirect authenticated Vendor/Admin users to their dashboard
  if (isAuthenticated && user?.role === Role.SUPER_ADMIN) {
    sessionStorage.setItem('lov_redirect_flash', 'Redirected to your dashboard');
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (isAuthenticated && (user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF)) {
    sessionStorage.setItem('lov_redirect_flash', 'Redirected to your dashboard');
    return <Navigate to="/vendor/dashboard" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Lord of Venues — Book Gaming &amp; Snooker Venues in India</title>
        <link rel="canonical" href="https://lordofvenues.com/" />
        <meta
          name="description"
          content="Book gaming, snooker, and kids play venues near you. Discover and reserve the best gaming arenas, snooker halls, and kids play areas across India."
        />
        <meta
          name="keywords"
          content="gaming venues, snooker halls, kids play areas, venue booking, gaming arenas, India"
        />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Lord of Venues — Book Gaming &amp; Snooker Venues in India"
        />
        <meta
          property="og:description"
          content="Discover and book verified gaming arenas, snooker halls, and kids play areas near you. Transparent pricing, instant confirmation."
        />
        <meta property="og:url" content="https://lordofvenues.com" />
        <meta property="og:site_name" content="Lord of Venues" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lord of Venues — Book Gaming, Snooker & Play Venues" />
        <meta
          name="twitter:description"
          content="Discover and book verified gaming arenas, snooker halls, and kids play areas near you."
        />
        {/* JSON-LD Structured Data */}
        <script key="org-schema" type="application/ld+json">
          {JSON.stringify(organizationJsonLd)}
        </script>
        <script key="website-schema" type="application/ld+json">
          {JSON.stringify(websiteJsonLd)}
        </script>
        <script key="faq-schema" type="application/ld+json">
          {JSON.stringify(faqPageJsonLd)}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 0 } }}>
        {/* Hero Section */}
        <HeroSection />

        <ScrollTransition variant="hero-to-venues" />

        {/* Venue Types Showcase */}
        <VenueTypeShowcase />

        <ScrollTransition variant="venues-to-how" />

        {/* How It Works */}
        <HowItWorksSection />

        <ScrollTransition variant="how-to-featured" />

        {/* Featured Venues */}
        <FeaturedVenuesCarousel />

        <ScrollTransition variant="featured-to-testimonials" />

        {/* Customer Testimonials */}
        <TestimonialsSection />

        {/* Browse by Location */}
        <LocationPickerSection />

        {/* Frequently Asked Questions */}
        <FAQSection faqs={faqs} />

        {/* Call to Action */}
        <CTASection />
      </Box>

      <MobileStickyBar />
    </>
  );
}
